import { request, useCommandRequest, useQueryRequest, useCommandWithFileRequest, validators } from '@komune-io/g2-utils';
import { imConfig, useAuthenticatedRequest, useAuth } from '@komune-io/g2-providers';
import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { __rest } from 'tslib';
import { styled, Typography, Stack, Box, InputLabel } from '@mui/material';
import { Popover } from '@komune-io/g2-notifications';
import { cx } from '@emotion/css';
import { FormComposable, useFormComposable } from '@komune-io/g2-composable';
import { useTranslation } from 'react-i18next';
import { TableV2, TableCellText, useTable, PopUp, TableCellProfile, TableCellLink } from '@komune-io/g2-layout';
import { Presentation, Link, MoreOptions, Chip, UserAvatar } from '@komune-io/g2-components';
import { useElementSize } from '@mantine/hooks';
import { useFormWithPartialFields, Form } from '@komune-io/g2-forms';

const useGetOrganizationRefs = (params) => {
    const { jwt, options, queryKey = 'organizationRefs' } = params !== null && params !== void 0 ? params : {};
    const getOrganizationRefs = useCallback(fetchOrganizationRefs(jwt), [jwt]);
    const query = useQuery(Object.assign({ queryKey: [queryKey], queryFn: getOrganizationRefs }, options));
    const map = useMemo(() => {
        var _a;
        if ((_a = query.data) === null || _a === void 0 ? void 0 : _a.items) {
            return new Map(query.data.items.map((o) => [o.id, o]));
        }
        return new Map();
    }, [query.data]);
    return {
        query: query,
        map: map
    };
};
const usePrefetchOrganizationRefs = async (params) => {
    const { jwt, options, queryKey = 'organizationRefs' } = params !== null && params !== void 0 ? params : {};
    const getOrganizationRefs = useCallback(fetchOrganizationRefs(jwt), [jwt]);
    const queryClient = useQueryClient();
    queryClient.prefetchQuery(Object.assign({ queryKey: [queryKey], queryFn: getOrganizationRefs }, options));
};
const fetchOrganizationRefs = (jwt) => async () => {
    var _a;
    const res = await request({
        url: `${imConfig().url}/organizationRefList`,
        method: 'POST',
        body: '[{}]',
        jwt: jwt
    });
    if (res) {
        return {
            items: ((_a = res[0]) === null || _a === void 0 ? void 0 : _a.items) || []
        };
    }
    else {
        return {
            items: []
        };
    }
};

const useOrganizationDisable = (params) => {
    const requestProps = useAuthenticatedRequest('im');
    return useCommandRequest('organizationDisable', requestProps, params);
};

const useGetOrganizations = (params) => {
    const requestProps = useAuthenticatedRequest('im');
    return useQueryRequest('organizationPage', requestProps, params);
};
const useGetOrganization = (params) => {
    const requestProps = useAuthenticatedRequest('im');
    return useQueryRequest('organizationGet', requestProps, params);
};
const getInseeOrganization = async (siret, apiUrl, jwt) => {
    const res = await request({
        url: `${apiUrl}/organizationGetFromInsee`,
        method: 'POST',
        body: JSON.stringify({
            siret: siret
        }),
        jwt: jwt
    });
    if (res) {
        return res[0].item;
    }
    else {
        return null;
    }
};
const useUpdateOrganization = (params) => {
    const requestProps = useAuthenticatedRequest('im');
    return useCommandRequest('organizationUpdate', requestProps, params);
};
const useCreateOrganization = (params) => {
    const requestProps = useAuthenticatedRequest('im');
    return useCommandRequest('organizationCreate', requestProps, params);
};
const useOrganizationUploadLogo = (params) => {
    const requestProps = useAuthenticatedRequest('im');
    return useCommandWithFileRequest('organizationUploadLogo', requestProps, params);
};

const useDeletableForm = (deletePrams) => {
    const { initialFields, blockedFields, additionalFields = [] } = deletePrams;
    const fields = useMemo(() => {
        const fields = [...initialFields, ...additionalFields];
        const fieldsFiltered = blockedFields
            ? fields.filter((field) => !blockedFields.includes(field.name))
            : fields;
        return fieldsFiltered;
    }, [initialFields, additionalFields, blockedFields]);
    return fields;
};

const useAddressFields = (params) => {
    const { fieldsOverride } = params || {};
    const { t } = useTranslation();
    const addressFields = useMemo(() => ({
        street: mergeFields({
            name: 'street',
            type: 'textField',
            label: t('g2.facultativeField', { label: t('g2.address') }),
            validator: validators.street(t)
        }, fieldsOverride === null || fieldsOverride === void 0 ? void 0 : fieldsOverride.street),
        postalCode: mergeFields({
            name: 'postalCode',
            type: 'textField',
            label: t('g2.facultativeField', { label: t('g2.postalCode') }),
            validator: validators.postalCode(t)
        }, fieldsOverride === null || fieldsOverride === void 0 ? void 0 : fieldsOverride.postalCode),
        city: mergeFields({
            name: 'city',
            type: 'textField',
            label: t('g2.facultativeField', { label: t('g2.city') }),
            validator: validators.city(t)
        }, fieldsOverride === null || fieldsOverride === void 0 ? void 0 : fieldsOverride.city)
    }), [t, fieldsOverride]);
    return {
        addressFields
    };
};

const mergeFields = (field, override) => {
    return Object.assign(Object.assign(Object.assign({}, field), override), { params: Object.assign(Object.assign({}, field.params), override === null || override === void 0 ? void 0 : override.params) });
};

const organizationToFlatOrganization = (org) => {
    var _a, _b, _c;
    const flat = Object.assign(Object.assign({}, org), { street: (_a = org.address) === null || _a === void 0 ? void 0 : _a.street, city: (_b = org.address) === null || _b === void 0 ? void 0 : _b.city, postalCode: (_c = org.address) === null || _c === void 0 ? void 0 : _c.postalCode, logo: undefined, logoUploaded: org.logo });
    if (!org.roles || org.roles.length <= 0)
        delete flat.roles;
    delete flat.address;
    return flat;
};
const flatOrganizationToOrganization = (flat, multipleRoles) => {
    var _a, _b, _c;
    const org = Object.assign(Object.assign({}, flat), { address: {
            street: (_a = flat.street) !== null && _a !== void 0 ? _a : '',
            city: (_b = flat.city) !== null && _b !== void 0 ? _b : '',
            postalCode: (_c = flat.postalCode) !== null && _c !== void 0 ? _c : ''
        }, 
        //@ts-ignore
        roles: multipleRoles ? flat.roles : [flat.roles] });
    delete org.street;
    delete org.city;
    delete org.postalCode;
    return org;
};

const siretValidation = (value, readOnly) => {
    if (readOnly)
        return undefined;
    const trimmed = value === null || value === void 0 ? void 0 : value.trim();
    if (!!trimmed && (trimmed === null || trimmed === void 0 ? void 0 : trimmed.length) !== 14 && (trimmed === null || trimmed === void 0 ? void 0 : trimmed.length) !== 0)
        return 'un numéro de siret doit être composé de 14 chiffres';
    return;
};

const useOrganizationFormFields = (params) => {
    const { formState, getInseeOrganization, setInseeOrganization, multipleRoles = true, fieldsOverride } = params !== null && params !== void 0 ? params : {};
    const [siretValid, setSiretValid] = useState(false);
    const [siretRef, setSiretRef] = useState(null);
    const { t } = useTranslation();
    const fetchOrganization = useCallback(async () => {
        if (getInseeOrganization && (formState === null || formState === void 0 ? void 0 : formState.values.siret)) {
            await getInseeOrganization(formState.values.siret).then((values) => {
                if (values) {
                    const flat = organizationToFlatOrganization(values);
                    for (const key in flat) {
                        if (flat[key] == null)
                            delete flat[key];
                    }
                    formState.setValues((oldValues) => (Object.assign(Object.assign({}, oldValues), flat)), false);
                    setSiretValid(true);
                    setInseeOrganization && setInseeOrganization(values);
                }
                else {
                    formState.setFieldError('siret', t('g2.siretNotFound'));
                }
            });
        }
    }, [
        formState === null || formState === void 0 ? void 0 : formState.values.siret,
        formState === null || formState === void 0 ? void 0 : formState.setValues,
        formState === null || formState === void 0 ? void 0 : formState.setFieldError,
        getInseeOrganization,
        t,
        setInseeOrganization
    ]);
    const { addressFields } = useAddressFields({
        //@ts-ignore
        fieldsOverride
    });
    const fields = useMemo(() => (Object.assign(Object.assign({ logo: mergeFields({
            name: 'logo',
            label: t('g2.facultativeField', { label: t('g2.logo') }),
            type: 'dropPicture',
            params: {
                alt: "Le logo de l'entreprise",
                height: '200px'
            }
        }, fieldsOverride === null || fieldsOverride === void 0 ? void 0 : fieldsOverride.logo), siret: mergeFields({
            name: 'siret',
            label: t('g2.siret'),
            type: 'textField',
            params: {
                textFieldType: 'search',
                iconPosition: 'end',
                noCheckOrClearIcon: true,
                validated: siretValid,
                // @ts-ignore
                ref: setSiretRef,
                onSearch: async () => {
                    setSiretValid(false);
                    if (!!formState && !(await formState.validateField('siret'))) {
                        await fetchOrganization();
                    }
                }
            },
            validator: (value) => { var _a; return siretValidation(value, (_a = fieldsOverride === null || fieldsOverride === void 0 ? void 0 : fieldsOverride.siret) === null || _a === void 0 ? void 0 : _a.readOnly); }
        }, fieldsOverride === null || fieldsOverride === void 0 ? void 0 : fieldsOverride.siret), name: mergeFields({
            name: 'name',
            type: 'textField',
            label: t('g2.name'),
            validator: validators.requiredField(t)
        }, fieldsOverride === null || fieldsOverride === void 0 ? void 0 : fieldsOverride.name), roles: mergeFields({
            name: 'roles',
            label: t('g2.roles'),
            type: 'select',
            params: {
                readOnlyType: 'chip',
                multiple: multipleRoles
            },
            validator: validators.requiredField(t)
        }, fieldsOverride === null || fieldsOverride === void 0 ? void 0 : fieldsOverride.roles) }, addressFields), { website: mergeFields({
            name: 'website',
            type: 'textField',
            label: t('g2.facultativeField', { label: t('g2.website') })
        }, fieldsOverride === null || fieldsOverride === void 0 ? void 0 : fieldsOverride.website), description: mergeFields({
            name: 'description',
            type: 'textField',
            label: t('g2.facultativeField', { label: t('g2.description') }),
            params: {
                multiline: true,
                rows: 6
            }
        }, fieldsOverride === null || fieldsOverride === void 0 ? void 0 : fieldsOverride.description) })), [
        t,
        addressFields,
        t,
        siretValid,
        fetchOrganization,
        formState === null || formState === void 0 ? void 0 : formState.validateField,
        multipleRoles,
        fieldsOverride
    ]);
    const fieldsArray = useMemo(() => Object.values(fields), [fields]);
    return {
        fields,
        siretValid,
        siretRef,
        fieldsArray
    };
};

// @ts-ignore
const StyledPopover = styled(Popover)({
    width: '80vw',
    maxWidth: '450px'
});
const OrganizationFactory = (props) => {
    var _a, _b;
    const { additionalFields = [], blockedFields = [], organization, onSubmit, getInseeOrganization, className, classes, styles, readOnly = false, isLoading = false, multipleRoles = true, fieldsOverride, setInseeOrganization, formState } = props, other = __rest(props, ["additionalFields", "blockedFields", "organization", "onSubmit", "getInseeOrganization", "className", "classes", "styles", "readOnly", "isLoading", "multipleRoles", "fieldsOverride", "setInseeOrganization", "formState"]);
    const [openSiretInfo, setOpenSiretInfo] = useState(!organization && !readOnly && !((_a = fieldsOverride === null || fieldsOverride === void 0 ? void 0 : fieldsOverride.siret) === null || _a === void 0 ? void 0 : _a.readOnly) && !isLoading);
    const { t } = useTranslation();
    useEffect(() => {
        var _a;
        if (!organization &&
            !readOnly &&
            !((_a = fieldsOverride === null || fieldsOverride === void 0 ? void 0 : fieldsOverride.siret) === null || _a === void 0 ? void 0 : _a.readOnly) &&
            !isLoading)
            setOpenSiretInfo(true);
    }, [organization, readOnly, (_b = fieldsOverride === null || fieldsOverride === void 0 ? void 0 : fieldsOverride.siret) === null || _b === void 0 ? void 0 : _b.readOnly, isLoading]);
    const onCloseSiretInfo = useCallback(() => setOpenSiretInfo(false), []);
    const { fieldsArray, siretRef } = useOrganizationFormFields(props);
    const finalFields = useDeletableForm({
        initialFields: fieldsArray,
        additionalFields: additionalFields,
        blockedFields: blockedFields
    });
    return (React.createElement(React.Fragment, null,
        React.createElement(FormComposable, Object.assign({}, other, { className: cx('AruiOrganizationFactory-root', className), fields: finalFields, formState: formState, isLoading: isLoading, readOnly: readOnly, onFocus: onCloseSiretInfo, sx: Object.assign({ width: '100%' }, other.sx) })),
        siretRef && (
        // @ts-ignore
        React.createElement(StyledPopover, { open: openSiretInfo, onClose: onCloseSiretInfo, anchorEl: siretRef, placement: 'bottom', className: cx('AruiOrganizationFactory-infoPopover', classes === null || classes === void 0 ? void 0 : classes.infoPopover), style: styles === null || styles === void 0 ? void 0 : styles.infoPopover },
            React.createElement(Typography, { variant: 'body1' }, t('g2.siretDescription'))))));
};

const useOrganizationFormState = (params) => {
    var _a, _b;
    const { createOrganizationOptions, getOrganizationOptions, organizationId, update, updateOrganizationOptions, defaultRoles, multipleRoles = true, myOrganization = false, uploadLogoOptions, formComposableParams, extendInitialValues } = params !== null && params !== void 0 ? params : {};
    const { keycloak, service } = useAuth();
    const queryClient = useQueryClient();
    const user = useMemo(() => {
        return service.getUser();
    }, [service.getUser]);
    const getOrganization = useGetOrganization({
        query: {
            id: (_a = (myOrganization ? user === null || user === void 0 ? void 0 : user.memberOf : organizationId)) !== null && _a !== void 0 ? _a : ''
        },
        options: Object.assign(Object.assign({}, getOrganizationOptions), { enabled: !!organizationId })
    });
    const organization = useMemo(() => { var _a; return (_a = getOrganization.data) === null || _a === void 0 ? void 0 : _a.item; }, [(_b = getOrganization.data) === null || _b === void 0 ? void 0 : _b.item]);
    const updateOrganizationOptionsMemo = useMemo(() => (Object.assign(Object.assign({}, updateOrganizationOptions), { onSuccess: (data, variables, context) => {
            var _a;
            getOrganization.refetch();
            queryClient.invalidateQueries({ queryKey: ['organizationRefs'] });
            queryClient.invalidateQueries({ queryKey: ['organizations'] });
            (_a = updateOrganizationOptions === null || updateOrganizationOptions === void 0 ? void 0 : updateOrganizationOptions.onSuccess) === null || _a === void 0 ? void 0 : _a.call(updateOrganizationOptions, data, variables, context);
        } })), [updateOrganizationOptions, getOrganization, queryClient.invalidateQueries]);
    const createOrganizationOptionsMemo = useMemo(() => (Object.assign(Object.assign({}, createOrganizationOptions), { onSuccess: (data, variables, context) => {
            var _a;
            queryClient.invalidateQueries({ queryKey: ['organizationRefs'] });
            queryClient.invalidateQueries({ queryKey: ['organizations'] });
            (_a = createOrganizationOptions === null || createOrganizationOptions === void 0 ? void 0 : createOrganizationOptions.onSuccess) === null || _a === void 0 ? void 0 : _a.call(createOrganizationOptions, data, variables, context);
        } })), [createOrganizationOptions, queryClient.invalidateQueries]);
    const updateOrganization = useUpdateOrganization({
        options: updateOrganizationOptionsMemo
    });
    const uploadLogo = useOrganizationUploadLogo({
        options: uploadLogoOptions
    });
    const createOrganization = useCreateOrganization({
        options: createOrganizationOptionsMemo
    });
    const updateOrganizationMemoized = useCallback(async (organization) => {
        organization.logo &&
            (await uploadLogo.mutateAsync({
                //@ts-ignore
                file: organization.logo,
                id: organization.id
            }));
        delete organization.logo;
        await updateOrganization.mutateAsync(organization);
    }, [updateOrganization.mutateAsync, uploadLogo.mutateAsync]);
    const createOrganizationMemoized = useCallback(async (organization) => {
        await createOrganization.mutateAsync(organization);
    }, [createOrganization.mutateAsync]);
    const onSubmitMemoized = useCallback(async (values) => {
        var _a;
        const onSubmit = update
            ? updateOrganizationMemoized
            : createOrganizationMemoized;
        await onSubmit(Object.assign(Object.assign(Object.assign({}, values), flatOrganizationToOrganization(values, multipleRoles)), { id: (_a = organization === null || organization === void 0 ? void 0 : organization.id) !== null && _a !== void 0 ? _a : '' }));
    }, [
        organization,
        multipleRoles,
        update,
        updateOrganizationMemoized,
        createOrganizationMemoized
    ]);
    const initialRoles = useMemo(() => {
        var _a;
        const roles = (_a = organization === null || organization === void 0 ? void 0 : organization.roles) !== null && _a !== void 0 ? _a : defaultRoles;
        return multipleRoles ? roles : roles === null || roles === void 0 ? void 0 : roles[0];
    }, [defaultRoles, organization === null || organization === void 0 ? void 0 : organization.roles, multipleRoles]);
    const initialValues = useMemo(() => (Object.assign(Object.assign(Object.assign({}, (organization
        ? //@ts-ignore
            organizationToFlatOrganization(organization)
        : undefined)), { 
        //@ts-ignore
        roles: initialRoles }), (extendInitialValues ? extendInitialValues(organization) : undefined))), [initialRoles, organization, extendInitialValues]);
    const formState = useFormComposable(Object.assign(Object.assign({}, formComposableParams), { onSubmit: onSubmitMemoized, formikConfig: {
            initialValues: initialValues
        } }));
    const getInseeOrganizationMemoized = useCallback(async (siret) => {
        return getInseeOrganization(siret, imConfig().url, keycloak.token);
    }, [keycloak.token]);
    return {
        formState,
        organization: organization,
        isLoading: getOrganization.isInitialLoading,
        getOrganization: getOrganization,
        getInseeOrganization: getInseeOrganizationMemoized
    };
};

const OrganizationTable = (props) => {
    const { tableState, getActions, page, setPage, tableActions, totalPages, noDataComponent, isLoading } = props, other = __rest(props, ["tableState", "getActions", "page", "setPage", "tableActions", "totalPages", "noDataComponent", "isLoading"]);
    const { t } = useTranslation();
    if (tableState.getTotalSize() === 0 && !isLoading)
        return (noDataComponent !== null && noDataComponent !== void 0 ? noDataComponent : (React.createElement(Typography, { align: 'center' }, t('g2.noData'))));
    return (React.createElement(TableV2, Object.assign({ page: page, handlePageChange: setPage, tableState: tableState, totalPages: totalPages, variant: 'grounded', isLoading: isLoading }, other)));
};

const useOrganizationColumns = (params) => {
    const { getActions } = params !== null && params !== void 0 ? params : {};
    const { t } = useTranslation();
    const columns = useMemo(() => (Object.assign({ name: {
            header: t('g2.organization'),
            id: 'name',
            cell: ({ row }) => (React.createElement(Presentation, { displayAvatar: false, label: row.original.name, imgSrc: row.original.logo })),
            className: 'nameColumn'
        }, address: {
            header: t('g2.address'),
            id: 'address',
            cell: ({ row }) => row.original.address ? (React.createElement(TableCellText, { value: `${row.original.address.street} ${row.original.address.postalCode} ${row.original.address.city}` })) : undefined,
            className: 'addressColumn'
        }, website: {
            header: t('g2.website'),
            id: 'website',
            cell: ({ row }) => (React.createElement(Link, { href: row.original.website, onClick: (e) => e.stopPropagation() }, row.original.website)),
            className: 'websiteColumn'
        } }, (getActions
        ? {
            actions: {
                id: 'moreoptions',
                cell: ({ row }) => (React.createElement(MoreOptions, { options: getActions(row.original), onClick: (e) => e.stopPropagation() }))
            }
        }
        : {}))), [t, getActions]);
    const columnsArray = useMemo(() => Object.values(columns), [columns]);
    return {
        columns,
        columnsArray
    };
};

const useOrganizationTableState = (params) => {
    const _a = params !== null && params !== void 0 ? params : {}, { columns, organizations = [], getActions } = _a, other = __rest(_a, ["columns", "organizations", "getActions"]);
    const base = useOrganizationColumns({
        getActions
    });
    return useTable(Object.assign({ data: organizations, columns: columns !== null && columns !== void 0 ? columns : base.columnsArray, getRowId: (row) => row.id }, other));
};

const AutomatedOrganizationTable = (props) => {
    var _a, _b, _c, _d;
    const { filters, getOrganizationsOptions, page, setPage, tableStateParams } = props, other = __rest(props, ["filters", "getOrganizationsOptions", "page", "setPage", "tableStateParams"]);
    const [localPage, setLocalPage] = useState(1);
    const getOrganizations = useGetOrganizations({
        query: Object.assign({ page: localPage - 1 }, filters),
        options: getOrganizationsOptions
    });
    const total = ((_a = getOrganizations.data) === null || _a === void 0 ? void 0 : _a.total)
        ? Math.ceil(((_b = getOrganizations.data) === null || _b === void 0 ? void 0 : _b.total) / 10)
        : 0;
    const tableState = useOrganizationTableState(Object.assign({ organizations: (_d = (_c = getOrganizations.data) === null || _c === void 0 ? void 0 : _c.items) !== null && _d !== void 0 ? _d : [] }, tableStateParams));
    return (React.createElement(OrganizationTable, Object.assign({ isLoading: !getOrganizations.isSuccess, tableState: tableState, totalPages: total && total > 1 ? total : undefined, page: page !== null && page !== void 0 ? page : localPage, setPage: setPage !== null && setPage !== void 0 ? setPage : setLocalPage }, other)));
};

const userToFlatUser = (user) => {
    var _a, _b, _c, _d;
    const flat = Object.assign(Object.assign({}, user), { street: (_a = user.address) === null || _a === void 0 ? void 0 : _a.street, city: (_b = user.address) === null || _b === void 0 ? void 0 : _b.city, postalCode: (_c = user.address) === null || _c === void 0 ? void 0 : _c.postalCode, memberOf: (_d = user.memberOf) === null || _d === void 0 ? void 0 : _d.id, roles: user.roles });
    delete flat.address;
    return flat;
};
const flatUserToUser = (flat) => {
    var _a, _b, _c, _d;
    // @ts-ignore
    const user = Object.assign(Object.assign({}, flat), { address: {
            street: (_a = flat.street) !== null && _a !== void 0 ? _a : '',
            city: (_b = flat.city) !== null && _b !== void 0 ? _b : '',
            postalCode: (_c = flat.postalCode) !== null && _c !== void 0 ? _c : ''
        }, memberOf: {
            id: (_d = flat.memberOf) !== null && _d !== void 0 ? _d : '',
            name: '',
            roles: []
        }, roles: transformRoles(flat.roles) });
    delete user.street;
    delete user.city;
    delete user.postalCode;
    return user;
};
function transformRoles(roles) {
    if (roles === undefined) {
        return [];
    }
    // Wrap roles in an array if it's not already an array
    const rolesArray = Array.isArray(roles) ? roles : [roles];
    // Flatten the roles array and transform objects with id property to their id string
    return rolesArray.map((role) => {
        if (typeof role === 'string') {
            return role;
        }
        else if (typeof role === 'object') {
            return role.identifier;
        }
        return '';
    });
}

const useUserDisable = (params) => {
    const requestProps = useAuthenticatedRequest('im');
    return useCommandRequest('userDisable', requestProps, params);
};

const useGetUsers = (params) => {
    const requestProps = useAuthenticatedRequest('im');
    return useQueryRequest('userPage', requestProps, params);
};
const useGetUser = (params) => {
    const requestProps = useAuthenticatedRequest('im');
    return useQueryRequest('userGet', requestProps, params);
};
const useUpdateUser = (params) => {
    const requestProps = useAuthenticatedRequest('im');
    return useCommandRequest('userUpdate', requestProps, params);
};
const useCreateUser = (params) => {
    const requestProps = useAuthenticatedRequest('im');
    return useCommandRequest('userCreate', requestProps, params);
};
const useUserUpdatePassword = (params) => {
    const requestProps = useAuthenticatedRequest('im');
    return useCommandRequest('userUpdatePassword', requestProps, params);
};
const useUserResetPassword = (params) => {
    const requestProps = useAuthenticatedRequest('im');
    return useCommandRequest('userResetPassword', requestProps, params);
};
const useUserUpdateEmail = (params) => {
    const requestProps = useAuthenticatedRequest('im');
    return useCommandRequest('userUpdateEmail', requestProps, params);
};
const userExistsByEmail = async (email, apiUrl, jwt) => {
    const res = await request({
        url: `${apiUrl}/userExistsByEmail`,
        method: 'POST',
        body: JSON.stringify({
            email: email
        }),
        jwt: jwt
    });
    if (res) {
        return res[0].item;
    }
    else {
        return undefined;
    }
};

const UserSummary = (props) => {
    const { fullName, roles, rolesOptions, onlyAvatar } = props;
    const renderTag = useMemo(() => {
        if (!roles || !rolesOptions)
            return undefined;
        if (Array.isArray(roles)) {
            return roles.map((value) => {
                const option = rolesOptions.find((o) => o.key === value);
                if (!(option === null || option === void 0 ? void 0 : option.label))
                    return undefined;
                return (React.createElement(Chip, { key: option.key.toString(), label: `${option === null || option === void 0 ? void 0 : option.label}`, color: option === null || option === void 0 ? void 0 : option.color }));
            });
        }
        const option = rolesOptions.find((o) => o.key === roles);
        if (!(option === null || option === void 0 ? void 0 : option.label))
            return undefined;
        return (React.createElement(Chip, { key: option.key.toString(), label: `${option === null || option === void 0 ? void 0 : option.label}`, color: option === null || option === void 0 ? void 0 : option.color }));
    }, [roles, rolesOptions]);
    return (React.createElement(Stack, { alignItems: 'center', sx: {
            gap: (theme) => theme.spacing(1)
        } },
        React.createElement(UserAvatar, { name: fullName, size: 'large' }),
        !onlyAvatar && (React.createElement(Stack, { alignItems: 'center', sx: {
                gap: (theme) => theme.spacing(0.5)
            } },
            React.createElement(Typography, { variant: 'subtitle1' }, fullName),
            React.createElement(Stack, { direction: 'row', sx: {
                    gap: (theme) => theme.spacing(0.5)
                } }, renderTag)))));
};

const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}/i;
const useUserFormFields = (params) => {
    const { fieldsOverride, checkEmailValidity, update = false, readOnly = false, organizationId, user, multipleRoles = true, getOrganizationUrl } = params !== null && params !== void 0 ? params : {};
    const [emailValid, setEmailValid] = useState(false);
    const [emailLoading, setEmailLoading] = useState(false);
    const { t } = useTranslation();
    const onCheckEmail = useCallback(async (email) => {
        if ((user === null || user === void 0 ? void 0 : user.email) !== email && checkEmailValidity) {
            setEmailLoading(true);
            const isTaken = await checkEmailValidity(email);
            setEmailLoading(false);
            if (isTaken === true) {
                setEmailValid(false);
                return t('g2.emailAlreadyUsed');
            }
            else if (isTaken === false) {
                setEmailValid(true);
            }
        }
        return undefined;
    }, [user === null || user === void 0 ? void 0 : user.email, checkEmailValidity, t]);
    const { addressFields } = useAddressFields({
        //@ts-ignore
        fieldsOverride
    });
    const fields = useMemo(() => {
        var _a, _b;
        return (Object.assign(Object.assign({ givenName: mergeFields({
                name: 'givenName',
                type: 'textField',
                label: t('g2.givenName'),
                validator: validators.requiredField(t)
            }, fieldsOverride === null || fieldsOverride === void 0 ? void 0 : fieldsOverride.givenName), familyName: mergeFields({
                name: 'familyName',
                type: 'textField',
                label: t('g2.familyName'),
                validator: validators.requiredField(t)
            }, fieldsOverride === null || fieldsOverride === void 0 ? void 0 : fieldsOverride.familyName), memberOf: mergeFields({
                name: 'memberOf',
                label: t('g2.memberOf'),
                type: 'select',
                params: {
                    getReadOnlyTextUrl: getOrganizationUrl
                }
            }, fieldsOverride === null || fieldsOverride === void 0 ? void 0 : fieldsOverride.memberOf), roles: mergeFields({
                name: 'roles',
                label: t('g2.roles'),
                type: 'select',
                params: {
                    readOnlyType: 'chip',
                    multiple: multipleRoles
                },
                validator: validators.requiredField(t)
            }, fieldsOverride === null || fieldsOverride === void 0 ? void 0 : fieldsOverride.roles) }, addressFields), { email: mergeFields({
                name: 'email',
                type: 'textField',
                label: t('g2.email'),
                params: {
                    textFieldType: 'email',
                    searchLoading: emailLoading,
                    validated: emailValid
                },
                validator: async (value) => {
                    var _a;
                    if ((_a = fieldsOverride === null || fieldsOverride === void 0 ? void 0 : fieldsOverride.email) === null || _a === void 0 ? void 0 : _a.readOnly)
                        return undefined;
                    const trimmed = (value !== null && value !== void 0 ? value : '').trim();
                    if (!trimmed)
                        return t('g2.completeTheEmail');
                    if (!emailRegex.test(trimmed))
                        return t('g2.enterAValidEmail');
                    return await onCheckEmail(trimmed);
                }
            }, fieldsOverride === null || fieldsOverride === void 0 ? void 0 : fieldsOverride.email), phone: mergeFields({
                name: 'phone',
                type: 'textField',
                label: t('g2.facultativeField', { label: t('g2.phone') })
            }, fieldsOverride === null || fieldsOverride === void 0 ? void 0 : fieldsOverride.phone), sendVerifyEmail: mergeFields({
                name: 'sendVerifyEmail',
                type: 'checkBox',
                label: t('g2.sendVerifyEmail'),
                params: {
                    disabled: (_a = fieldsOverride === null || fieldsOverride === void 0 ? void 0 : fieldsOverride.sendVerifyEmail) === null || _a === void 0 ? void 0 : _a.readOnly
                }
            }, fieldsOverride === null || fieldsOverride === void 0 ? void 0 : fieldsOverride.sendVerifyEmail), sendResetPassword: mergeFields({
                name: 'sendResetPassword',
                type: 'checkBox',
                label: t('g2.sendResetPassword'),
                params: {
                    disabled: (_b = fieldsOverride === null || fieldsOverride === void 0 ? void 0 : fieldsOverride.sendResetPassword) === null || _b === void 0 ? void 0 : _b.readOnly
                }
            }, fieldsOverride === null || fieldsOverride === void 0 ? void 0 : fieldsOverride.sendResetPassword) }));
    }, [
        t,
        fieldsOverride,
        organizationId,
        multipleRoles,
        addressFields,
        emailLoading,
        onCheckEmail,
        update,
        readOnly
    ]);
    const fieldsArray = useMemo(() => Object.values(fields), [fields]);
    return {
        emailLoading,
        emailValid,
        fields,
        fieldsArray
    };
};

const UserResetPasswordForm = (props) => {
    const { userId, onSubmit, classes, styles, className, SubmitButtonRef } = props, other = __rest(props, ["userId", "onSubmit", "classes", "styles", "className", "SubmitButtonRef"]);
    const { t } = useTranslation();
    const partialFields = useMemo(() => [
        {
            name: 'password',
            validator: validators.password(t)
        },
        {
            name: 'passwordCheck',
            validator: validators.passwordCheck(t)
        }
    ], [t]);
    const onSubmitMemoized = useCallback(async (values, formikHelpers) => {
        if (onSubmit) {
            const feedback = await onSubmit({
                id: userId,
                password: values.password
            });
            if (feedback) {
                formikHelpers.resetForm();
            }
        }
    }, [onSubmit, userId]);
    const formState = useFormWithPartialFields({
        fields: partialFields,
        onSubmit: onSubmitMemoized,
        formikConfig: {
            enableReinitialize: true
        }
    });
    useEffect(() => {
        const element = SubmitButtonRef === null || SubmitButtonRef === void 0 ? void 0 : SubmitButtonRef.current;
        if (element) {
            element.onclick = formState.submitForm;
        }
    }, [SubmitButtonRef === null || SubmitButtonRef === void 0 ? void 0 : SubmitButtonRef.current, formState.submitForm]);
    const form = useMemo(() => [
        {
            key: 'password',
            name: 'password',
            type: 'textfield',
            label: t('g2.newPassword'),
            textFieldProps: {
                textFieldType: 'password'
            }
        },
        {
            key: 'passwordCheck',
            name: 'passwordCheck',
            type: 'textfield',
            label: t('g2.passwordCheck'),
            textFieldProps: {
                textFieldType: 'password'
            }
        }
    ], [t]);
    return (React.createElement(Stack, Object.assign({ flexWrap: 'wrap', justifyContent: 'space-between', direction: 'column', className: cx('AruiUserResetPassword-root', className) }, other),
        React.createElement(Form, { className: cx('AruiUserResetPassword-form', 'mainFormLeft', classes === null || classes === void 0 ? void 0 : classes.form), style: styles === null || styles === void 0 ? void 0 : styles.form, fields: form, formState: formState })));
};

const UserResetPasswordFormAutomated = (props) => {
    const { userId, userUpdatePasswordOptions } = props, other = __rest(props, ["userId", "userUpdatePasswordOptions"]);
    const userUpdatePassword = useUserUpdatePassword({
        options: userUpdatePasswordOptions
    });
    const handleResetPasswordSubmit = useCallback(async (cmd) => {
        const result = await userUpdatePassword.mutateAsync(cmd);
        return !!result;
    }, [userUpdatePassword.mutateAsync]);
    return (React.createElement(UserResetPasswordForm, Object.assign({ userId: userId, onSubmit: handleResetPasswordSubmit }, other)));
};

const ChoicedResetPassword = (props) => {
    const { resetPasswordType = 'email', userId, userUpdatePasswordOptions } = props, other = __rest(props, ["resetPasswordType", "userId", "userUpdatePasswordOptions"]);
    const [open, setOpen] = useState(false);
    const [mutating, setMutating] = useState(false);
    const [error, setError] = useState(false);
    const submitRef = useRef(null);
    const { t } = useTranslation();
    const onToggle = useCallback(() => {
        setOpen((open) => !open);
    }, []);
    const onSuccess = useCallback((data, varaibles, context) => {
        var _a;
        setMutating(false);
        onToggle();
        (_a = userUpdatePasswordOptions === null || userUpdatePasswordOptions === void 0 ? void 0 : userUpdatePasswordOptions.onSuccess) === null || _a === void 0 ? void 0 : _a.call(userUpdatePasswordOptions, data, varaibles, context);
    }, [onToggle, userUpdatePasswordOptions === null || userUpdatePasswordOptions === void 0 ? void 0 : userUpdatePasswordOptions.onSuccess]);
    const onMutate = useCallback((varaibles) => {
        var _a;
        setMutating(true);
        (_a = userUpdatePasswordOptions === null || userUpdatePasswordOptions === void 0 ? void 0 : userUpdatePasswordOptions.onMutate) === null || _a === void 0 ? void 0 : _a.call(userUpdatePasswordOptions, varaibles);
    }, [userUpdatePasswordOptions === null || userUpdatePasswordOptions === void 0 ? void 0 : userUpdatePasswordOptions.onMutate]);
    const onError = useCallback((error, varaibles, context) => {
        var _a;
        setMutating(false);
        setError(true);
        (_a = userUpdatePasswordOptions === null || userUpdatePasswordOptions === void 0 ? void 0 : userUpdatePasswordOptions.onError) === null || _a === void 0 ? void 0 : _a.call(userUpdatePasswordOptions, error, varaibles, context);
    }, [userUpdatePasswordOptions === null || userUpdatePasswordOptions === void 0 ? void 0 : userUpdatePasswordOptions.onError]);
    const userResetPassword = useUserResetPassword({
        options: Object.assign(Object.assign({}, userUpdatePasswordOptions), { onMutate,
            onSuccess,
            onError })
    });
    const actions = useMemo(() => [
        {
            label: t('g2.cancel'),
            key: 'cancelPopupButton',
            onClick: onToggle,
            variant: 'text'
        },
        {
            label: t('g2.confirm'),
            key: 'confirmPopupButton',
            ref: submitRef,
            isLoading: mutating,
            fail: error,
            onClick: () => {
                if (resetPasswordType === 'email') {
                    userResetPassword.mutate({ id: userId });
                }
            }
        }
    ], [onToggle, mutating, error, t, userId]);
    return (React.createElement(Box, null,
        React.createElement(InputLabel, { sx: {
                marginBottom: (theme) => theme.spacing(1),
                fontWeight: 600,
                color: '#000000'
            } }, t('g2.password')),
        React.createElement(Link, { onClick: onToggle, sx: { color: '#1E88E5', cursor: 'pointer' } }, t('g2.changePassword')),
        React.createElement(PopUp, { onClose: onToggle, open: open, actions: actions },
            React.createElement(Typography, { variant: 'h6', style: { marginBottom: '15px' } }, t('g2.passwordChangement')),
            resetPasswordType === 'email' ? (React.createElement(Typography, { variant: 'body1' }, t('g2.passwordEmail'))) : (React.createElement(UserResetPasswordFormAutomated, Object.assign({ userUpdatePasswordOptions: Object.assign(Object.assign({}, userUpdatePasswordOptions), { onMutate,
                    onSuccess,
                    onError }), SubmitButtonRef: submitRef, userId: userId }, other))))));
};

const UserFactory = (props) => {
    var _a, _b, _c, _d;
    const { user, onSubmit, className, readOnly = false, formState, additionalFields, organizationId, isLoading = false, blockedFields = [], readOnlyRolesOptions, multipleRoles = true, checkEmailValidity, formExtension, fieldsOverride, update = false, userId, resetPasswordType, choicedResetPasswordProps } = props, other = __rest(props, ["user", "onSubmit", "className", "readOnly", "formState", "additionalFields", "organizationId", "isLoading", "blockedFields", "readOnlyRolesOptions", "multipleRoles", "checkEmailValidity", "formExtension", "fieldsOverride", "update", "userId", "resetPasswordType", "choicedResetPasswordProps"]);
    const { ref, width } = useElementSize();
    const { fieldsArray } = useUserFormFields(props);
    delete other.getOrganizationUrl;
    const definitivBlockedFields = useMemo(() => {
        var _a, _b;
        return [
            //@ts-ignore
            ...(!((_b = (_a = fieldsOverride === null || fieldsOverride === void 0 ? void 0 : fieldsOverride.memberOf) === null || _a === void 0 ? void 0 : _a.params) === null || _b === void 0 ? void 0 : _b.options) && !organizationId
                ? ['memberOf']
                : []),
            //@ts-ignore
            ...(update || readOnly
                ? ['sendResetPassword', 'sendVerifyEmail']
                : []),
            ...blockedFields
        ];
    }, [blockedFields, fieldsOverride, organizationId, update, readOnly]);
    const finalFields = useDeletableForm({
        initialFields: fieldsArray,
        additionalFields: additionalFields,
        blockedFields: definitivBlockedFields
    });
    return (React.createElement(Stack, Object.assign({}, other, { className: cx('AruiUserFactory-root', className), ref: ref, flexDirection: width < 450 ? 'column' : 'row', justifyContent: 'center', sx: {
            width: '100%',
            gap: (theme) => (width < 450 ? theme.spacing(3) : theme.spacing(12))
        } }),
        React.createElement(UserSummary, { onlyAvatar: width < 450, fullName: `${(_a = formState.values.givenName) !== null && _a !== void 0 ? _a : ''} ${(_b = formState.values.familyName) !== null && _b !== void 0 ? _b : ''}`, roles: formState.values.roles, rolesOptions: 
            //@ts-ignore
            readOnlyRolesOptions !== null && readOnlyRolesOptions !== void 0 ? readOnlyRolesOptions : (_d = (_c = fieldsOverride === null || fieldsOverride === void 0 ? void 0 : fieldsOverride.roles) === null || _c === void 0 ? void 0 : _c.params) === null || _d === void 0 ? void 0 : _d.options }),
        React.createElement(Stack, { sx: {
                gap: (theme) => theme.spacing(3)
            } },
            React.createElement(FormComposable, { className: 'AruiUserFactory-form', fields: finalFields, formState: formState, isLoading: isLoading, readOnly: readOnly, sx: {
                    width: '100%',
                    flexGrow: 1,
                    maxWidth: '450px'
                } }),
            React.createElement(React.Fragment, null,
                formExtension,
                userId && resetPasswordType && (React.createElement(ChoicedResetPassword, Object.assign({ resetPasswordType: resetPasswordType, userId: userId }, choicedResetPasswordProps)))))));
};

const useUserFormState = (params) => {
    var _a, _b;
    const { multipleRoles = true, organizationId, defaultRoles = [], createUserOptions, getUserOptions, updateUserOptions, userUpdateEmailOptions, update = false, myProfile = false, userId, formComposableParams, extendInitialValues } = params !== null && params !== void 0 ? params : {};
    const { keycloak, service } = useAuth();
    const queryClient = useQueryClient();
    const keycloakUser = useMemo(() => {
        return service.getUser();
    }, [service.getUser]);
    const getUser = useGetUser({
        query: {
            id: (_a = (myProfile && keycloakUser ? keycloakUser.id : userId)) !== null && _a !== void 0 ? _a : ''
        },
        options: Object.assign(Object.assign({}, getUserOptions), { enabled: !!userId })
    });
    const user = useMemo(() => { var _a; return (_a = getUser.data) === null || _a === void 0 ? void 0 : _a.item; }, [getUser.data]);
    const updateUserOptionsMemo = useMemo(() => (Object.assign(Object.assign({}, updateUserOptions), { onSuccess: (data, variables, context) => {
            var _a;
            getUser.refetch();
            queryClient.invalidateQueries({ queryKey: ['users'] });
            (_a = updateUserOptions === null || updateUserOptions === void 0 ? void 0 : updateUserOptions.onSuccess) === null || _a === void 0 ? void 0 : _a.call(updateUserOptions, data, variables, context);
        } })), [updateUserOptions, getUser, queryClient.invalidateQueries]);
    const createUserOptionsMemo = useMemo(() => (Object.assign(Object.assign({}, createUserOptions), { onSuccess: (data, variables, context) => {
            var _a;
            queryClient.invalidateQueries({ queryKey: ['users'] });
            (_a = createUserOptions === null || createUserOptions === void 0 ? void 0 : createUserOptions.onSuccess) === null || _a === void 0 ? void 0 : _a.call(createUserOptions, data, variables, context);
        } })), [createUserOptions, queryClient.invalidateQueries]);
    const updateUser = useUpdateUser({
        options: updateUserOptionsMemo
    });
    const createUser = useCreateUser({
        options: createUserOptionsMemo
    });
    const updateEmail = useUserUpdateEmail({
        options: userUpdateEmailOptions
    });
    const updateUserMemoized = useCallback(async (user) => {
        var _a, _b;
        const results = [];
        //@ts-ignore
        results.push(updateUser.mutateAsync(Object.assign(Object.assign({}, user), { memberOf: (_a = user.memberOf) === null || _a === void 0 ? void 0 : _a.id })));
        if (((_b = getUser.data) === null || _b === void 0 ? void 0 : _b.item.email) !== user.email) {
            results.push(updateEmail.mutateAsync({
                email: user.email,
                id: user.id
            }));
        }
        const res = await Promise.all(results);
        for (const it in res) {
            const result = res[it];
            if (!result)
                return false;
        }
        return true;
    }, [updateUser.mutateAsync, updateEmail.mutateAsync, (_b = getUser.data) === null || _b === void 0 ? void 0 : _b.item.email]);
    const createUserMemoized = useCallback(async (user) => {
        var _a, _b;
        //@ts-ignore
        const res = await createUser.mutateAsync(Object.assign(Object.assign({}, user), { memberOf: (_b = (_a = user.memberOf) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : organizationId }));
        if (res) {
            return true;
        }
        else {
            return false;
        }
    }, [createUser.mutateAsync]);
    const checkEmailValidity = useCallback(async (email) => {
        return userExistsByEmail(email, imConfig().url, keycloak.token);
    }, [keycloak.token]);
    const onSubmitMemoized = useCallback(async (values) => {
        var _a, _b;
        const onSubmit = update ? updateUserMemoized : createUserMemoized;
        if (onSubmit) {
            await onSubmit(Object.assign(Object.assign(Object.assign({}, values), flatUserToUser(values)), { id: (_a = user === null || user === void 0 ? void 0 : user.id) !== null && _a !== void 0 ? _a : '', phone: (_b = values.phone) === null || _b === void 0 ? void 0 : _b.replaceAll(' ', '') }));
        }
    }, [user, multipleRoles, update, updateUserMemoized, createUserMemoized]);
    const initialValues = useMemo(() => {
        var _a;
        return (Object.assign(Object.assign(Object.assign({ 
            //@ts-ignore
            memberOf: organizationId, sendVerifyEmail: true, sendResetPassword: true }, (user ? userToFlatUser(user) : undefined)), { roles: multipleRoles
                ? (user === null || user === void 0 ? void 0 : user.roles) || defaultRoles
                : // @ts-ignore
                    (user === null || user === void 0 ? void 0 : user.roles) && ((_a = user === null || user === void 0 ? void 0 : user.roles) === null || _a === void 0 ? void 0 : _a.length) > 0
                        ? user === null || user === void 0 ? void 0 : user.roles[0]
                        : defaultRoles[0] }), (extendInitialValues ? extendInitialValues(user) : undefined)));
    }, [user, multipleRoles, defaultRoles, extendInitialValues]);
    const formState = useFormComposable(Object.assign(Object.assign({}, formComposableParams), { onSubmit: onSubmitMemoized, formikConfig: {
            initialValues: initialValues
        } }));
    return {
        formState,
        checkEmailValidity,
        user: user,
        isLoading: getUser.isInitialLoading,
        getUser: getUser
    };
};

const UserTable = (props) => {
    const { totalPages, page, setPage, noDataComponent, isLoading, tableState } = props, other = __rest(props, ["totalPages", "page", "setPage", "noDataComponent", "isLoading", "tableState"]);
    if (tableState.getTotalSize() === 0 && noDataComponent && !isLoading)
        return noDataComponent;
    return (React.createElement(TableV2, Object.assign({ page: page, tableState: tableState, handlePageChange: setPage, totalPages: totalPages, variant: 'grounded', isLoading: isLoading }, other)));
};

const useUserColumns = (params) => {
    const { getActions, getOrganizationUrl, hasOrganizations, users } = params !== null && params !== void 0 ? params : {};
    const { t } = useTranslation();
    const columns = useMemo(() => (Object.assign(Object.assign({ givenName: {
            header: t('g2.name'),
            id: 'givenName',
            cell: ({ row }) => (React.createElement(TableCellProfile, { value: {
                    familyName: row.original.familyName,
                    givenName: row.original.givenName
                } })),
            className: 'givenNameColumn'
        }, address: {
            header: t('g2.address'),
            id: 'address',
            cell: ({ row }) => row.original.address ? (React.createElement(TableCellText, { value: `${row.original.address.street} ${row.original.address.postalCode} ${row.original.address.city}` })) : undefined,
            className: 'addressColumn'
        }, email: {
            header: t('g2.email'),
            id: 'email',
            cell: ({ row }) => React.createElement(TableCellText, { value: row.original.email }),
            className: 'emailColumn'
        } }, ((!!users && !!users[0] && !!users[0].memberOf) || hasOrganizations
        ? {
            memberOf: {
                header: t('g2.organization'),
                id: 'memberOf',
                cell: ({ row }) => {
                    var _a, _b, _c, _d;
                    if (!!getOrganizationUrl && ((_a = row.original.memberOf) === null || _a === void 0 ? void 0 : _a.id)) {
                        return (React.createElement(TableCellLink, { value: {
                                label: (_b = row.original.memberOf) === null || _b === void 0 ? void 0 : _b.name,
                                url: getOrganizationUrl((_c = row.original.memberOf) === null || _c === void 0 ? void 0 : _c.id)
                            } }));
                    }
                    return React.createElement(TableCellText, { value: (_d = row.original.memberOf) === null || _d === void 0 ? void 0 : _d.name });
                },
                className: 'memberOfColumn'
            }
        }
        : {})), (getActions
        ? {
            actions: {
                id: 'moreoptions',
                cell: ({ row }) => (React.createElement(MoreOptions, { options: getActions(row.original), onClick: (e) => e.stopPropagation() }))
            }
        }
        : {}))), [users, getOrganizationUrl, t, hasOrganizations, getActions]);
    const columnsArray = useMemo(() => Object.values(columns), [columns]);
    return {
        columns,
        columnsArray
    };
};

const useUserTableState = (params) => {
    const _a = params !== null && params !== void 0 ? params : {}, { columns, users = [], getOrganizationUrl, hasOrganizations = false, getActions } = _a, other = __rest(_a, ["columns", "users", "getOrganizationUrl", "hasOrganizations", "getActions"]);
    const base = useUserColumns({
        getOrganizationUrl,
        hasOrganizations,
        users,
        getActions
    });
    return useTable(Object.assign({ data: users, columns: columns !== null && columns !== void 0 ? columns : base.columnsArray, getRowId: (row) => row.id }, other));
};

const AutomatedUserTable = (props) => {
    var _a, _b, _c, _d;
    const { filters, getUsersOptions, page, setPage, tableStateParams, getOrganizationUrl, hasOrganizations } = props, other = __rest(props, ["filters", "getUsersOptions", "page", "setPage", "tableStateParams", "getOrganizationUrl", "hasOrganizations"]);
    const [localPage, setLocalPage] = useState(1);
    const getUsers = useGetUsers({
        query: Object.assign({ page: localPage - 1 }, filters),
        options: getUsersOptions
    });
    const total = Math.ceil(((_b = (_a = getUsers.data) === null || _a === void 0 ? void 0 : _a.total) !== null && _b !== void 0 ? _b : 0) / 10);
    const tableState = useUserTableState(Object.assign({ users: (_d = (_c = getUsers.data) === null || _c === void 0 ? void 0 : _c.items) !== null && _d !== void 0 ? _d : [], getOrganizationUrl,
        hasOrganizations }, tableStateParams));
    return (React.createElement(UserTable, Object.assign({ page: page !== null && page !== void 0 ? page : localPage, setPage: setPage !== null && setPage !== void 0 ? setPage : setLocalPage, isLoading: !getUsers.isSuccess, tableState: tableState, totalPages: total && total > 1 ? total : undefined }, other)));
};

export { AutomatedOrganizationTable, AutomatedUserTable, OrganizationFactory, OrganizationTable, UserFactory, UserResetPasswordForm, UserResetPasswordFormAutomated, UserSummary, UserTable, flatOrganizationToOrganization, flatUserToUser, getInseeOrganization, mergeFields, organizationToFlatOrganization, siretValidation, useAddressFields, useCreateOrganization, useCreateUser, useGetOrganization, useGetOrganizationRefs, useGetOrganizations, useGetUser, useGetUsers, useOrganizationColumns, useOrganizationDisable, useOrganizationFormFields, useOrganizationFormState, useOrganizationTableState, useOrganizationUploadLogo, usePrefetchOrganizationRefs, useUpdateOrganization, useUpdateUser, useUserColumns, useUserDisable, useUserFormFields, useUserFormState, useUserResetPassword, useUserTableState, useUserUpdateEmail, useUserUpdatePassword, userExistsByEmail, userToFlatUser };
//# sourceMappingURL=index.es.js.map
